/**
 * @name normalize
 * @description normalized application functions
 */
const normalize = {
  /**
   * @createActionTypes
   * @description create actionTypes object
   * @param {Array<String>} types type values
   * @param {String} base the base of the string
   * @return {Object} the actionTypes
   * @example
   * const base = 'GAMEDAY/ORDERS/';
   * const types = ['SUBMIT_ORDER'];
   *
   * const actionTypes = createActionTypes(types, base); // { SUBMIT_ORDER: 'GAMEDAY/ORDERS/SUBMIT_ORDER'}
   */
  createActionTypes: (types, base) => {
    const isNotString = val => !val || typeof val !== 'string';
    if (isNotString(base)) {
      throw new Error('Non string base passed to createActionTypes');
    }

    const returnTypes = {};
    if (Array.isArray(types)) {
      types.forEach(type => {
        if (!isNotString(type)) {
          returnTypes[type] = `${base}${type}`;
        }
      });
    }
    return returnTypes;
  },
  /**
   * @desc indexes array list by indexer (default = 'id')
   * @method arrayToIndexed
   * @param {object} param
   * @param {array} param.array array to be indexed
   * @param {(string|function)} [param.indexer=id]  string key single level deep, function to return key
   * @param {function} normalizer  function to return modified item
   * @return {object} returns indexed list
   */
  arrayToIndexed: ({ array, indexer = 'id', normalizer }) => {
    if (!array || !Array.isArray(array) || array.length < 1) return {};
    const indexedList = {};
    const newArray = array.slice();
    let normalizeVal;
    if (typeof normalizer === 'function' || normalizer instanceof Function) {
      normalizeVal = normalizer;
    } else {
      normalizeVal = value => value;
    }
    newArray.forEach((item, index, arr) => {
      if (typeof indexer === 'function' || indexer instanceof Function) {
        const keyValue = indexer(item, index, arr);
        indexedList[keyValue] = normalizeVal(item);
      } else {
        indexedList[item[indexer]] = normalizeVal(item);
      }
    });
    return indexedList;
  },

  /**
   * @desc Function for combining sliced reducers into one
   * @method combineMiniReducers
   * @param {object} [reducers = {}] the reducers to combine
   * @param {object} [defaultStore = {}] the default state for the store
   * @return single redux store for redux's combineReducers function
   * @example
   * const defaultStore = { one: 1 }
   * const reducerOne = (state = {}, action) => state
   * const reducerTwo = (state = {}, action) => state
   *
   * const myStore = combineMiniReducers({reducerOne, reducerTwo}, defaultStore);
   * const reduxStore = combineReducer({ myStore })
   */
  combineMiniReducers: (reducers = {}, defaultStore = {}) => {
    const reducerKeys = Object.values(reducers);
    return (state = defaultStore, action) => {
      let currentState = state;

      reducerKeys.forEach(reducer => {
        currentState = reducer(currentState, action);
      });

      return currentState;
    };
  },

  /**
   * @desc limit amount of times function is called in succession
   * @method debounce
   * @param {function} fn function to be called
   * @param {number} time amount of time to pass before calling function
   * @return passed function
   * @example
   * const consoleMe = val => console.log(val);
   * window.addEventListener('resize', debounce(consoleMe, 500));
   */
  debounce: (fn, time) => {
    let timeout;
    if (fn) {
      // eslint-disable-next-line func-names
      return function (...args) {
        const functionCall = () => fn.apply(this, args);
        window.clearTimeout(timeout);
        timeout = window.setTimeout(functionCall, time);
      };
    }
    return null;
  },

  /**
   * @desc pushed indexed obect items to array
   * @method indexedToArray
   * @param {object} params
   * @param {object} params.indexedList indexed object
   * @param {string} [params.sort] direction to sort list (default: 'asc')
   * @param {string} [params.sortField] field to sort by (default: 'id')
   * @return {array} returns new array with previously indexed objects
   */
  indexedToArray: ({ indexedList, sort = null, sortField = 'id' }) => {
    if (!indexedList) return [];
    let newArr = Object.values(indexedList);
    if (sort) {
      newArr = normalize.listSorter({
        array: newArr,
        sort,
        sortField
      });
    }
    return newArr;
  },

  /**
   * @desc Function for sorting  list
   * @method listSorter
   * @param {object} params
   * @param {array} params.array array to be sorted
   * @param {string} [params.sort] ' asc' or 'desc' (default 'asc')
   * @param {string} [params.sortField] field to sort by (single layer deep, default 'id')
   * @return new sorted array
   */
  listSorter: ({ array, sort = 'asc', sortField = 'id' }) => {
    if (!array || !Array.isArray(array)) {
      // eslint-disable-next-line no-console
      console.warn('non-array supplied to listSorter');
      return [];
    }
    if (sort.toLowerCase() === 'asc') {
      return [...array].sort((a, b) => {
        if (a[sortField] < b[sortField]) return -1;
        if (a[sortField] > b[sortField]) return 1;
        return 0;
      });
    }
    return [...array].sort((a, b) => {
      if (a[sortField] > b[sortField]) return -1;
      if (a[sortField] < b[sortField]) return 1;
      return 0;
    });
  },

  /**
   * @method normalizeArray
   * @param {String} keyProperty
   * @param {Array} array
   */
  normalizeArray: (keyProperty, array) =>
    array.reduce((result, element) => {
      const updatedResult = result;
      updatedResult[element[keyProperty]] = element;
      return updatedResult;
    }, {}),

  /**
   * @method normalizeVersionName
   * @param {String} name
   * @return {String} the normalized name
   */
  normalizeVersionName: name => (name && name[0] === 'v' ? name : `v${name}`)
};

export default normalize;
